"use client";

import type { NewsSectionComponent } from "../../types";
import { ArrowRight } from "lucide-react";
import { NewsItemCard } from "../../components/home/cards/NewsItemCard";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, resolveImageValue } from "../../utils/imageOptions";

/**
 * News section renderer: one featured article beside a compact news list.
 * The renderer is source-agnostic — `featured`/`items` may be inline-baked in
 * the page payload or filled at request time by the host's dynamic-component
 * resolution (content_items); by render time they look identical. Renders
 * nothing when both sides are empty, and drops the featured column (list goes
 * full-width) when only items exist.
 */
export function NewsSectionRenderer({ component }: { component: NewsSectionComponent }) {
  const locale = useLocale();
  const featured = component.content?.featured;
  const items = Array.isArray(component.content?.items) ? component.content.items : [];
  const hasFeatured =
    !!featured &&
    Boolean(featured.title || featured.excerpt || resolveLinkHref(featured.href));
  if (!hasFeatured && items.length === 0) return null;

  const { src, options } = resolveImageValue(featured?.image);
  const featuredImage = src || "/media/welcome_background.jpg";
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const featuredHref = featured ? resolveLinkHref(featured.href) ?? "/" : "/";
  return (
    <section className="bcc-news-section relative mx-auto max-w-7xl px-4 md:px-8 py-6 md:py-10">
      {/* Tablette : gaps resserrés (20px) entre les deux colonnes et entre les cartes,
          le contenu respire ; desktop garde 32px. */}
      <div className={mergeClassNames("grid grid-cols-1 gap-8 md:gap-5 lg:gap-8", hasFeatured ? "md:grid-cols-2" : "md:grid-cols-1")}>
        {hasFeatured && featured && (
          <article className="overflow-hidden rounded-sm bg-white">
            <div
              className={mergeClassNames(
                "relative w-full bg-slate-200 aspect-[4/3] md:aspect-auto md:h-[400px] lg:h-[440px] max-h-[440px] overflow-hidden",
                overrideProps.className
              )}
              style={overrideProps.style}
            >
              <RuntimeImage
                src={featuredImage}
                alt={featured.title}
                className="absolute inset-0 h-full w-full object-cover object-center"
                style={objectPositionStyle}
                priority
              />
            </div>
            <div className="p-5 px-4 md:p-6 space-y-4 bg-[#EDF7FC]">
              {/* Tablette : titre un cran plus petit (22px) ; mobile et desktop gardent 24px. */}
              <h3 className="text-[#0069B4] font-semibold leading-tight md:leading-snug text-2xl md:text-[1.375rem] lg:text-2xl" style={{ fontFamily: "Nunito Sans" }}>
                {featured.title}
              </h3>
              <p className="text-base xl:text-lg text-slate-600 line-clamp-4" style={{ fontFamily: "Nunito Sans" }}>
                {featured.excerpt}
              </p>
              <RuntimeLink
                className="inline-flex items-center gap-2 rounded-full bg-[#F9A21B] px-6 py-2.5 text-sm xl:text-base font-semibold uppercase tracking-wide text-white hover:bg-[#e2920e] w-fit"
                href={featuredHref}
              >
                {t(locale, "readMoreShort")}
                <ArrowRight className="h-4 w-4" />
              </RuntimeLink>
            </div>
          </article>
        )}

        {/* On desktop the column stretches to the featured card's height and
            splits into equal-height rows (auto-rows-fr) so the news cards fill
            the same vertical space as the left column with even gaps. On mobile
            the cards keep their natural stacked height. */}
        <div className="grid grid-cols-1 gap-4 content-start md:auto-rows-fr md:h-full md:content-stretch md:gap-5 lg:gap-8">
          {items.map((n) => (
            <NewsItemCard key={n.id || n.title} title={n.title} excerpt={n.excerpt} image={n.image || ""} href={n.href} />
          ))}
        </div>
      </div>
    </section>
  );
}


